/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import {
    DetailsList,
    DetailsRow,
    IColumn,
    IDetailsListStyleProps,
    IDetailsListStyles,
    IDetailsRowStyleProps,
    IDetailsRowStyles,
    IDragDropContext,
    IDragDropEvents,
    SelectionMode
} from '@fluentui/react/lib/DetailsList';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import React from 'react';

export type DragAndDropListProps<T> = {
    /**
     * Items of the drag and drop list.
     */
    items: T[];

    /**
     * A callback function to render items.
     */
    onRenderItem: (item: T) => JSX.Element;

    /**
     * A callback function to update the items order.
     */
    onEditItems: (items: T[]) => void;

    /**
     * List's width.
     */
    width?: number;

    canDragAndDrop?: boolean;

    /**
     * optional columns parameter to replace the default one column.
     */
    listColumns?: IColumn[];
};

const rowStyles = classNamesFunction<IDetailsRowStyleProps, IDetailsRowStyles>()({
    root: {
        borderBottom: 'none',
        backgroundColor: 'transparent',
        $nest: {
            '&:hover,&:focus,&:select': {
                backgroundColor: 'transparent'
            }
        }
    },
    cell: { padding: '0px' }
});

const detailsListStyles = classNamesFunction<IDetailsListStyleProps, IDetailsListStyles>()({
    root: { width: '100%' },
    headerWrapper: { display: 'none' }
});

/**
 * A component rendering a list of items supporting dragging and dropping.
 */
export const DragAndDropList = observer(<T extends object>(props: DragAndDropListProps<T>) => {
    const { items, onEditItems, onRenderItem, width = 200, canDragAndDrop = true, listColumns = [] } = props;

    const [draggedItem, setDraggedItem] = React.useState<T>();

    const columns: IColumn[] = listColumns.length
        ? listColumns
        : [
              {
                  key: 'items',
                  minWidth: width,
                  name: '',
                  onRender: onRenderItem
              }
          ];

    const getDragDropEvents = (): IDragDropEvents => ({
        canDrop: (_dropContext?: IDragDropContext, _dragContext?: IDragDropContext) => canDragAndDrop,
        canDrag: (_item?: T) => canDragAndDrop,
        onDragEnter: (_item?: T, _event?: DragEvent) => '',
        onDragLeave: (_item?: T, _event?: DragEvent) => {},
        onDrop: (item?: T, _event?: DragEvent) => {
            if (draggedItem) {
                insertBeforeItem(item);
            }
        },
        onDragStart: (item?: T, _itemIndex?: number, _selectedItems?: T[], _event?: MouseEvent) => {
            setDraggedItem(item);
        },
        onDragEnd: (_item?: T, _event?: DragEvent) => {
            setDraggedItem(undefined);
        }
    });

    const insertBeforeItem = (item: T): void => {
        const draggedItems = [draggedItem];

        const insertIndex = items.indexOf(item);
        const newItems = items.filter(itm => draggedItems.indexOf(itm) === -1);

        newItems.splice(insertIndex, 0, ...draggedItems);

        onEditItems(newItems);
    };

    return (
        <DetailsList
            onShouldVirtualize={() => false}
            setKey="items"
            items={items}
            columns={columns}
            dragDropEvents={getDragDropEvents()}
            selectionMode={SelectionMode.none}
            styles={detailsListStyles}
            onRenderRow={rowProps => <DetailsRow {...rowProps} data-is-focusable aria-busy styles={rowStyles} />}
        />
    );
});
